import moment from 'moment'
import { useLocation } from 'react-router'
import numeral from 'numeraljs'
import FileSaver from 'file-saver'

import { ValidationChecks } from '../api-client/api-types'
import { getLocalItem, setLocalItem } from '../local-client/local-client'
import { TOKEN } from '../api-client/constants'

export function hashCode(str = '') {
  let hash = 5381
  let i = str.length

  while (i) {
    // eslint-disable-next-line
    hash = (hash * 33) ^ str.charCodeAt(--i)
  }

  // eslint-disable-next-line
  return hash >>> 0
}

export function getToken(): string {
  const token = getLocalItem(TOKEN)
  return typeof token === 'string' ? token : ''
}

export function setToken(token: string): void {
  setLocalItem(TOKEN, token)
}

export function getAnomalyBreakdowUrl({
  date,
  metricID,
}: {
  date: string
  metricID: string
}): string {
  return `/explain/anomaly/${metricID}/${moment(date, 'DD/MM/YYYY').format(
    'DD-MM-YYYY',
  )}`
}

export function getAnomalyType(
  anomaly: Anomaly,
): null | 'explained' | 'not-interesting' {
  const { explainedAnomaly, anomalyNotInteresting } = anomaly
  const type = anomalyNotInteresting ? 'not-interesting' : null
  return type === null && explainedAnomaly ? 'explained' : type
}

export function useQuery(s = '') {
  const useString = useLocation().search
  return new URLSearchParams(s || useString)
}

export const cap = (word: string): string =>
  `${word.charAt(0).toUpperCase()}${word.substring(1)}`

export const copyString = (toCopy: string | string[]): void => {
  const input = document.createElement('textarea')
  input.style.opacity = '0'
  input.style.pointerEvents = 'none'
  input.style.position = 'absolute'
  input.value = Array.isArray(toCopy) ? toCopy.join('\r\n') : toCopy
  document.body.append(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

const checks = {
  ALL_LOWER_CASE: /[A-Z]/,
  NO_SPECIAL_CHARS: /[?=&]/,
  NO_SPECIAL_CHARS_LANDING_PAGE: /[?=&]/,
  NO_SPACES: /\s/,
  LIMIT_URL_LENGTH: 1024,
  LIMIT_QUERY_LENGTH: 255,
}

export const isValidInput = (
  useValue: string | string[],
  validation?: ValidationChecks[] | undefined | null,
): boolean => {
  const useValues = Array.isArray(useValue) ? useValue : [useValue]
  const valid = useValues.filter((value) => {
    if (value && value !== '' && validation) {
      return !validation.some((item) => {
        if (item.enabled) {
          if (
            item.name === 'LIMIT_URL_LENGTH' ||
            item.name === 'LIMIT_QUERY_LENGTH'
          ) {
            const limit =
              Object.prototype.hasOwnProperty.call(item, 'value') &&
              item.value &&
              item.value !== ''
                ? item.value
                : checks[item.name]
            return value.length > limit
          }
          if (Object.prototype.hasOwnProperty.call(checks, item.name)) {
            const pattern = checks[item.name]
            return value.match(new RegExp(pattern)) !== null
          }
        }
        return false
      })
    }
    return true
  })

  return valid.length === useValues.length
}

const replaceChecks = {
  // REPLACE_SPACES_WITH_UNDERSCORES: [/\s/, '_'],
  NO_SPECIAL_CHARS: [/[?=&]/, ''],
  REPLACE_SPACES_WITH: [/\s/, '_'],
  CONVERT_LOWER_CASE: [/[A-Z]/, (a: string): string => a.toLowerCase()],
}

export const prepareInput = (
  value: string,
  validation?: ValidationChecks[] | undefined | null,
): string => {
  if (value && value !== '' && validation) {
    let result = value

    validation.forEach((item) => {
      if (
        item.enabled &&
        Object.prototype.hasOwnProperty.call(replaceChecks, item.name)
      ) {
        const pattern = replaceChecks[item.name][0]
        const useValue = Object.prototype.hasOwnProperty.call(item, 'value')
          ? item.value
          : replaceChecks[item.name][1]
        result = result.replace(new RegExp(pattern, 'ig'), useValue)
      }
      return value
    })
    return result
  }
  return value
}

// Return title for dimensions based on mDN value
export const getDimensionTitle = (mDN, dimensions): string => {
  if (mDN.indexOf('_') === -1) {
    return mDN
  }
  const data = mDN.split('_')
  const dimensionTitle =
    dimensions && Object.prototype.hasOwnProperty.call(dimensions, data[1])
      ? dimensions[data[1]]
      : data[1]
  const title = data.slice(2).join('_')
  return `${dimensionTitle
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()} = ${title}`
}

// High level validation for URL
export const isValidUrl = (url) => {
  return (
    url &&
    url !== '' &&
    url.length > 1 &&
    url.search(/(https:\/\/)|(http:\/\/)/gi) !== -1
  )
}

// return all values that match search value in array with items of assoc arrays
export const getItemByKeyValue = (
  items: any[] | undefined | null,
  key: string,
  value: string | number | undefined,
  returnIndex = false,
  ignoreCase = true,
): any => {
  if (
    typeof value === 'undefined' ||
    items === null ||
    typeof items === 'undefined'
  ) {
    return -1
  }
  const searchVal = ignoreCase
    ? JSON.stringify(value).toLowerCase()
    : JSON.stringify(value)
  let indexCounter = -1
  const found = items.find((item, index) => {
    let res
    if (ignoreCase) {
      res =
        typeof item[key] !== 'undefined' &&
        JSON.stringify(item[key]).toLowerCase() === searchVal
    } else {
      res =
        typeof item[key] !== 'undefined' &&
        JSON.stringify(item[key]) === searchVal
    }

    if (res) {
      indexCounter = index
    }
    return res
  })
  if (returnIndex) {
    return indexCounter
  }
  return found || -1
}

export const removeByKeyValue = (
  items: any[],
  key: string,
  value: string,
): any[] => {
  return items.filter((item) => {
    return item[key] && item[key].toLowerCase() !== value.toLowerCase()
  })
}

export const replaceByKeyValue = (
  items: any[],
  key: string,
  value: string,
  newValue: any,
  appendWhenNotFound: any = false,
): any[] => {
  let found = false
  const result = items.map((item) => {
    if (item[key] && item[key].toLowerCase() === value.toLowerCase()) {
      found = true
      return newValue
    }
    return item
  })
  if (appendWhenNotFound && !found) {
    return [...result, newValue]
  }
  return result
}

export const getMetricName = (metrics: GaMetric[], metric: string): string => {
  const found = metrics.find((item) => {
    return item.apiName === metric
  })
  return found ? found.displayName : ''
}

export const getAnomalyDateRange = (
  dateRange: string,
): { from: string; to: string } => {
  let from = ''
  const now = new Date(Date.now())
  let to: string = moment(now).subtract(1, 'days').format('YYYYMMDD')

  if (dateRange === 'Last 30days') {
    from = moment(now).subtract(31, 'days').format('YYYYMMDD')
  } else if (dateRange === 'Last 90days') {
    from = moment(now).subtract(91, 'days').format('YYYYMMDD')
  } else if (dateRange === 'Year') {
    from = moment(now).subtract(366, 'days').format('YYYYMMDD')
  } else if (dateRange.indexOf(' - ') !== -1) {
    // eslint-disable-next-line
    ;[from, to] = dateRange.split(' - ')
    // from = moment(from, 'YYYYMMDD').format('YYYYMMDD')
    // to = moment(to, 'YYYYMMDD').format('YYYYMMDD')
  }

  return {
    to,
    from,
  }
}

export const paginateData = (data: any, counter: number): any[] => {
  return data.reduce((acc, item, index) => {
    const resultArray = acc.concat()
    const chunkIndex = Math.floor(index / counter)

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []
    }
    resultArray[chunkIndex].push(item)
    return resultArray
  }, [])
}

// Sorts array by key and order value, with special rules for diff and date key names
export const sortData = (
  data: any,
  key: string,
  orderAsc: boolean,
  getValueFn?: any,
  ignoreCase?: boolean,
): any[] => {
  if (!data || (data && data.length === 0)) {
    return data
  }
  const newData = data.concat()
  const fn =
    getValueFn ||
    ((row) => {
      return ignoreCase && typeof row[key] === 'string'
        ? row[key].toLowerCase()
        : row[key]
    })

  // diff key is quite special case...
  if (key === 'diff') {
    newData.sort((previous, next) => {
      const prevDiff = previous.actual - previous.expected
      const nextDiff = next.actual - next.expected
      return prevDiff < nextDiff ? -1 : 1
    })
  } else {
    newData.sort((previous, next) => {
      const p = fn(previous, orderAsc)
      const n = fn(next, orderAsc)
      return p < n ? 1 : -1
    })
  }
  if (orderAsc) {
    return newData.reverse()
  }
  return newData
}

export const getValidationCheck = (
  validationChecks,
  key,
  defaultValue = false,
) => {
  const defaultValidation = {
    enabled: defaultValue,
    name: key,
  }

  if (!validationChecks) {
    return defaultValidation
  }

  const found = validationChecks.find((item) => item.name === key)
  if (key === 'REPLACE_SPACES_WITH') {
    if (!found) {
      return { enabled: true, name: 'REPLACE_SPACES_WITH', value: '_' }
    }
    const value = Object.prototype.hasOwnProperty.call(found, 'value')
      ? found.value
      : '_'
    return { ...found, value }
  }
  if (key === 'LIMIT_URL_LENGTH') {
    if (!found) {
      return {
        enabled: true,
        name: 'LIMIT_URL_LENGTH',
        value: checks.LIMIT_URL_LENGTH,
      }
    }
    const value = Object.prototype.hasOwnProperty.call(found, 'value')
      ? found.value
      : '_'
    return { ...found, value }
  }
  if (key === 'LIMIT_QUERY_LENGTH') {
    if (!found) {
      return {
        enabled: true,
        name: 'LIMIT_QUERY_LENGTH',
        value: checks.LIMIT_QUERY_LENGTH,
      }
    }
    const value = Object.prototype.hasOwnProperty.call(found, 'value')
      ? found.value
      : '_'
    return { ...found, value }
  }
  if (key === 'COPY_FROM_SEPARATOR') {
    // eslint-disable-next-line
    // debugger
    if (!found) {
      return { enabled: true, name: 'COPY_FROM_SEPARATOR', value: '|' }
    }
    const value = Object.prototype.hasOwnProperty.call(found, 'value')
      ? found.value
      : '_'
    return { ...found, value }
  }
  if (!found) {
    return defaultValidation
  }
  return found
}

export const getValidationChecksObject = (
  generatedStructure,
  isHomepage = false,
  override: {
    name: string
    enabled: boolean
    value?: string
  }[] = [],
) => {
  if (!generatedStructure) {
    return null
  }

  const { validationChecks } = generatedStructure

  if (isHomepage) {
    return [
      {
        enabled: true,
        name: 'NO_SPACES',
      },
      getValidationCheck(validationChecks, 'NO_SPECIAL_CHARS_LANDING_PAGE'),
    ]
  }
  // remove landing page validation options
  let useChecks = removeByKeyValue(
    validationChecks,
    'name',
    'NO_SPECIAL_CHARS_LANDING_PAGE',
  )
  override.forEach((item) => {
    useChecks = replaceByKeyValue(useChecks, 'name', item.name, item, true)
  })
  const rsw = getValidationCheck(validationChecks, 'REPLACE_SPACES_WITH')
  return replaceByKeyValue(useChecks, 'name', 'REPLACE_SPACES_WITH', rsw, true)
}

export const isEmpty = (value: any, forceZero = false): boolean => {
  return value === '' || value === null || (forceZero && value === 0)
}

export const formatValueForDisplay = (
  value: any,
  units: string,
  hideEmpty = false,
  hideNegativeValues = false,
): string => {
  let displayValue = ''
  const returnEmpty =
    (hideNegativeValues && value < 0) ||
    (hideEmpty &&
      value === 0 &&
      (units === 'percentage' || units === 'duration'))

  if (returnEmpty) {
    return displayValue
  }

  if (units === 'percentage') {
    displayValue = `${numeral(value).format('0,0')}%`
  } else if (units === 'duration') {
    const time = Math.floor(value)
    const minutes = Math.floor(time / 60)
    const seconds = time - minutes * 60
    displayValue = `${minutes}m ${seconds}s`
  } else if (units === 'int') {
    displayValue = numeral(value).format('0,0')
  } else {
    displayValue = numeral(value).format('0,0.[0]a')
  }
  return displayValue
}

export const makeHexString = (val: string): string => {
  return val
    .replace('#', '')
    .toUpperCase()
    .replace(/(?!([0-9A-F]))./gi, '')
    .slice(0, 6)
}

// removed duplicate items in array of objects or strings
export function returnUnique(arr: any[], key: null | string = null) {
  if (key !== null) {
    return arr.reduce((prev, curr) => {
      if (getItemByKeyValue(prev, key, curr[key], true) === -1) {
        prev.push(curr)
      }
      return prev
    }, [])
  }
  const seen = {}
  return arr.filter((item) => {
    const hasSeen = Object.prototype.hasOwnProperty.call(seen, item)
    if (!hasSeen) {
      seen[item] = true
    }
    return !hasSeen
  })
}

export const getDefaultOption = (
  data,
  dataKey,
  keyValue,
  defaultValue: any = null,
) => {
  if (data && data[dataKey] && data[dataKey].length > 0) {
    const _val = getItemByKeyValue(data[dataKey], 'name', keyValue)
    if (_val !== -1) {
      return _val.value
    }
  }
  return defaultValue
}

export const getCsvString = (data: any[]) => {
  if (data.length === 0) return ''

  const allKeys = data.flatMap((row) => Object.keys(row))
  const headers = Array.from(new Set(allKeys))
  const outArr = [headers.join(',')]

  data.forEach((row) => {
    const vals: any[] = []

    headers.forEach((header) => {
      vals.push(row[header] !== undefined ? row[header] : '')
    })

    const stringVals = vals.map(
      (r) => `"${r.toString().replace(/\"/gi, '""')}"`,
    )
    outArr.push(stringVals.join(','))
  })

  return outArr.join('\n')
}

export const downloadUsersCsv = async (
  data: string,
  accountName: string,
): Promise<boolean> => {
  if (typeof data === 'string') {
    const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8' })
    const now = new Date(Date.now())
    await FileSaver.saveAs(
      blob,
      `User list - ${accountName} - ${moment(now).format('YYYY-MM-DD')}.csv`,
    )
  }

  return true
}

export function getKeyFromMetricValue(str: string | string[]) {
  if (Array.isArray(str)) {
    return str.map((s) => getKeyFromMetricValue(s))
  }
  return str.replace(':', '')
}
